import { zodResolver } from '@hookform/resolvers/zod';
import React, { PropsWithChildren, Ref, useImperativeHandle } from 'react';
import './form.css';
import {
  DefaultValues,
  FormProvider,
  SubmitErrorHandler,
  useForm,
  UseFormProps,
  UseFormReset,
  UseFormSetValue,
} from 'react-hook-form';
import { ZodType } from 'zod';

export const Form = <T extends Record<string, any>>({
                                                      onSubmit,
                                                      onFormError,
                                                      children,
                                                      schema,
                                                      className,
                                                      defaultValues,
                                                      values,
                                                      buttonSubmitRef,
                                                      methodsRef,
                                                    }: FcFormProps<T>) => {

  const {
    register,
    handleSubmit,
    formState,
    setValue,
    setError,
    getValues,
    clearErrors,
    watch,
    reset,
  } = useForm<T>({
    resolver: zodResolver(schema),
    defaultValues,
    values,
  } as UseFormProps<T>);

  // Exposition des méthodes "reset" et "setValue" au composant parent
  useImperativeHandle(methodsRef, () => ({
    reset,
    setValue,
  }), [reset, setValue]);

  return (
    <FormProvider
      schema={schema}
      register={register}
      formState={formState}
      setValue={setValue}
      setError={setError}
      getValues={getValues}
      clearErrors={clearErrors}
      watch={watch}
      reset={reset}
    >
      <form
        onSubmit={handleSubmit(onSubmit, onFormError as SubmitErrorHandler<T>)}
        className={className ?? ''}
      >
        {children}
        {(!!buttonSubmitRef) && <button type={'submit'} hidden ref={buttonSubmitRef} />}
      </form>
    </FormProvider>
  );
};

interface FcFormProps<T extends Record<string, any>> extends PropsWithChildren {
  onSubmit: (result: T) => void;
  onFormError?: SubmitErrorHandler<T>;
  schema: ZodType<any, any, any>;
  className?: string;
  defaultValues?: DefaultValues<T>;
  values?: DefaultValues<T>;
  buttonSubmitRef?: Ref<HTMLButtonElement>;
  methodsRef?: Ref<FormMethodsRef<T>>;
}

export interface FormMethodsRef<T extends Record<string, any>> {
  reset: UseFormReset<T>;
  setValue: UseFormSetValue<T>;
}